<template>
  <div class="profile-page-shell">
    <Edit
      v-if="getCustomer"
      :editDialog="editDialog"
      :editCustomer="getCustomer"
      @close="editDialog = false"
      @submit="loadData"
    />
    <ParentInvoiceCreate
      v-if="getCustomer"
      :createDialog="createParentInvoiceDialog"
      :parent="getCustomer"
      @close="createParentInvoiceDialog = false"
      @submit="invoiceCreated"
    />
    <TuitionBillingPreview
      v-if="getCustomer"
      v-model="billingPreviewDialog"
      :customer-id="getCustomer.id"
    />
    <ReEnrollment
      v-if="getCustomer"
      v-model="reEnrollmentDialog"
      :customer-id="getCustomer.id"
      @completed="reEnrollmentCompleted"
    />
    <v-dialog v-model="parentIdDialog" max-width="390">
      <v-card class="parent-id-dialog">
        <v-card-title>Open parent profile</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="openParentId">
            <v-text-field ref="parentIdInput" v-model.trim="parentIdInput"
              label="Parent ID" prepend-inner-icon="mdi-pound" type="number"
              inputmode="numeric" pattern="[0-9]*" min="1" autofocus
              hide-details outlined />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="parentIdDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="parentIdLoading"
            :disabled="!parentIdInput" @click="openParentId">Go</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="profile-topbar">
      <v-btn text color="primary" class="profile-back-button" to="/parents/list">
        <v-icon left>mdi-arrow-left</v-icon><span>Parents</span>
      </v-btn>
      <div class="profile-record-navigation" aria-label="Navigate parent records">
        <v-btn
          icon
          small
          :disabled="!adjacentParents.previous"
          aria-label="Previous parent"
          @click="moveBack"
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <button type="button" class="profile-record-navigation__id" @click="showParentIdDialog">
          Parent #{{ getCustomer && getCustomer.localId ? getCustomer.localId : "—" }}
        </button>
        <v-btn
          icon
          small
          :disabled="!adjacentParents.next"
          aria-label="Next parent"
          @click="moveNext"
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <v-skeleton-loader
      v-if="loading"
      type="card, list-item-three-line"
      class="profile-skeleton"
    />

    <template v-else-if="getCustomer && getCustomer.id">
      <v-alert
        v-if="getCustomer.alert"
        class="profile-alert"
        color="primary"
        text
        border="left"
        icon="mdi-alert-circle-outline"
      >
        <div class="profile-alert__title">Important note</div>
        <div>{{ getCustomer.note }}</div>
      </v-alert>

      <v-card class="profile-hero-card" elevation="0">
        <div class="profile-hero">
          <div class="profile-hero__identity">
            <div class="profile-hero__avatar">{{ initials(getCustomer.name) }}</div>
            <div>
              <div class="profile-hero__eyebrow">Parent profile</div>
              <h1 class="profile-hero__title">{{ getCustomer.name }}</h1>
              <div class="profile-hero__meta">
                <span
                  ><v-icon size="15">mdi-clock-outline</v-icon> Added
                  {{ getCustomer.createdAt | moment("from", "now") }}</span
                >
                <span v-if="getCustomer.address"
                  ><v-icon size="15">mdi-map-marker-outline</v-icon>
                  {{ getCustomer.city }}, {{ getCustomer.state }}</span
                >
              </div>
            </div>
          </div>
          <div class="profile-hero__actions">
            <v-btn
              class="profile-hero__quick-invoice-action"
              v-if="checkRightStatus(6)"
              color="primary"
              depressed
              @click="createParentInvoiceDialog = true"
            >
              <v-icon left size="19">mdi-receipt-text-plus-outline</v-icon>Quick
              fee invoice
            </v-btn>
            <v-btn class="profile-hero__edit-action" outlined color="primary" @click="editDialog = true"
              ><v-icon left size="18">mdi-pencil-outline</v-icon>Edit</v-btn
            >
            <v-menu bottom left offset-y>
              <template v-slot:activator="{ on, attrs }"
                ><v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  outlined
                  color="primary"
                  aria-label="More parent actions"
                  ><v-icon>mdi-dots-horizontal</v-icon></v-btn
                ></template
              >
              <v-list dense class="action-menu">
                <v-list-item @click="reEnrollmentDialog = true"
                  ><v-list-item-icon
                    ><v-icon size="19"
                      >mdi-account-multiple-plus-outline</v-icon
                    ></v-list-item-icon
                  ><v-list-item-title
                    >Re-enroll students</v-list-item-title
                  ></v-list-item
                >
                <v-divider />
                <v-list-item @click="viewOpenInvoice"
                  ><v-list-item-icon
                    ><v-icon size="19"
                      >mdi-file-document-outline</v-icon
                    ></v-list-item-icon
                  ><v-list-item-title
                    >Open invoices</v-list-item-title
                  ></v-list-item
                >
                <v-list-item @click="viewBalanceLetter"
                  ><v-list-item-icon
                    ><v-icon size="19"
                      >mdi-email-outline</v-icon
                    ></v-list-item-icon
                  ><v-list-item-title
                    >Balance letter</v-list-item-title
                  ></v-list-item
                >
                <SyncBalance />
                <Statement :CustomerId="getCustomer.id" />
                <PaymentCollectLink
                  :id="getCustomer.id"
                  :customer="getCustomer"
                />
              </v-list>
            </v-menu>
          </div>
        </div>

        <v-divider />
        <v-card-text class="profile-content-grid">
          <div class="profile-info-grid">
            <section class="profile-info-card">
              <div class="profile-info-label">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon
                >Dad
              </div>
              <div class="profile-info-name">
                {{ getCustomer.fatherName || "Not provided" }}
              </div>
              <a
                v-if="getCustomer.fatherPhoneNumber"
                :href="`tel:${getCustomer.fatherPhoneNumber}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-phone-outline</v-icon
                >{{ getCustomer.fatherPhoneNumber | USPhoneNumber }}</a
              >
              <span v-else class="profile-info-empty">No phone number</span>
              <a
                v-if="getCustomer.fatherEmailAddress"
                :href="`mailto:${getCustomer.fatherEmailAddress}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-email-outline</v-icon
                >{{ getCustomer.fatherEmailAddress }}</a
              >
              <span v-else class="profile-info-empty">No email address</span>
            </section>

            <section class="profile-info-card">
              <div class="profile-info-label">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon
                >Mom
              </div>
              <div class="profile-info-name">
                {{ getCustomer.motherName || "Not provided" }}
              </div>
              <a
                v-if="getCustomer.motherPhoneNumber"
                :href="`tel:${getCustomer.motherPhoneNumber}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-phone-outline</v-icon
                >{{ getCustomer.motherPhoneNumber | USPhoneNumber }}</a
              >
              <span v-else class="profile-info-empty">No phone number</span>
              <a
                v-if="getCustomer.motherEmailAddress"
                :href="`mailto:${getCustomer.motherEmailAddress}`"
                class="profile-info-link"
                ><v-icon size="16">mdi-email-outline</v-icon
                >{{ getCustomer.motherEmailAddress }}</a
              >
              <span v-else class="profile-info-empty">No email address</span>
            </section>

            <section class="profile-info-card profile-info-card--full">
              <div class="profile-info-label">
                <v-icon color="primary" size="18">mdi-map-marker-outline</v-icon
                >Address
              </div>
              <button
                v-if="getCustomer.address"
                type="button"
                class="profile-address-button"
                @click="openGoogleMap"
              >
                <strong>{{ getCustomer.address }}</strong>
                <span
                  >{{ getCustomer.city }}, {{ getCustomer.state }}
                  {{ getCustomer.zip }}</span
                >
                <small
                  >Open in Google Maps
                  <v-icon size="13">mdi-open-in-new</v-icon></small
                >
              </button>
              <span v-else class="profile-info-empty">No address provided</span>
            </section>
          </div>

          <aside class="profile-summary-panel">
            <div class="profile-summary-label">Current balance</div>
            <div class="profile-summary-value" :class="{ overdue: isOverLimit }">
              {{ getCustomer.balance | currency }}
            </div>
            <div class="profile-summary-breakdown">
              <div>
                <span>Parent responsibility</span
                ><strong>{{ studentFee.totalCustomerFee | currency }}</strong>
              </div>
              <div v-if="studentFee.totalCharity > 0">
                <span>Masjid responsibility</span
                ><strong>{{ studentFee.totalCharity | currency }}</strong>
              </div>
              <div v-if="studentFee.totalZakat > 0">
                <span>Zakat responsibility</span
                ><strong>{{ studentFee.totalZakat | currency }}</strong>
              </div>
              <div v-if="studentFee.totalPayrollDeduction > 0">
                <span>Payroll deduction</span
                ><strong>{{ studentFee.totalPayrollDeduction | currency }}</strong>
              </div>
              <div v-if="studentFee.totalDiscount > 0">
                <span>Discounts</span
                ><strong>{{ studentFee.totalDiscount | currency }}</strong>
              </div>
              <div v-if="advanceSummary.available > 0" class="profile-summary-advance">
                <span>Advance tuition available</span
                ><strong>{{ advanceSummary.available | currency }}</strong>
              </div>
            </div>
            <div v-if="paymentMethods.length" class="profile-summary-tags">
              <span class="profile-summary-label">Payment methods</span>
              <v-chip
                v-for="method in paymentMethods"
                :key="method"
                x-small
                color="primary"
                outlined
                >{{ method }}</v-chip
              >
            </div>
            <v-btn
              class="profile-billing-preview"
              outlined
              color="primary"
              depressed
              @click="billingPreviewDialog = true"
            >
              <v-icon left size="18">mdi-receipt-text-clock-outline</v-icon>
              Preview billing
            </v-btn>
          </aside>
        </v-card-text>
      </v-card>

      <div class="profile-tabs-wrap">
        <v-tabs
          v-model="active"
          color="primary"
          slider-color="primary"
          show-arrows
          class="profile-tabs"
        >
          <v-tab v-for="tab in tabs" :key="tab.label"
            ><v-icon left size="18">{{ tab.icon }}</v-icon
            >{{ tab.label }}</v-tab
          >
        </v-tabs>
      </div>

      <v-tabs-items v-model="active" touchless class="profile-tab-content">
        <v-tab-item><Dashboard /></v-tab-item>
        <v-tab-item
          ><StudentList showBalance="hide" :CustomerId="getCustomer.id" :includeFormer="true"
        /></v-tab-item>
        <v-tab-item
          ><InvoiceList
            toggleStatus="hide"
            :internalUse="true"
            :showAll="true"
            :CustomerId="getCustomer.id"
        /></v-tab-item>
        <v-tab-item><PaymentList :CustomerId="getCustomer.id" /></v-tab-item>
        <v-tab-item
          ><AdvancePaymentList :CustomerId="getCustomer.id" :showAll="true"
        /></v-tab-item>
        <v-tab-item
          ><Subscription toggleStatus="hide" :CustomerId="getCustomer.id"
        /></v-tab-item>
        <v-tab-item
          ><AttachmentList TypeId="1" :RelationId="getCustomer.id"
        /></v-tab-item>
      </v-tabs-items>
    </template>

    <v-alert v-else type="warning" text
      >We could not load this parent profile.</v-alert
    >
  </div>
</template>

<script>
import customerService from "@/modules/Customer/service.js";
import advancePaymentService from "@/modules/AdvancePayment/service.js";
import { mapGetters, mapActions } from "vuex";
import Hashids from "hashids";
const hashids = new Hashids();
const Dashboard = () => import("@/modules/Customer/components/Dashboard.vue");
const InvoiceList = () => import("@/modules/Invoice/components/List");
const StudentList = () => import("@/modules/Student/components/List");
const AttachmentList = () => import("@/modules/Attachment/components/List");
const AdvancePaymentList = () =>
  import("@/modules/AdvancePayment/components/List");
const PaymentList = () =>
  import("@/modules/Invoice/Payment/components/List.vue");
const PaymentCollectLink = () =>
  import("@/modules/Customer/components/PaymentCollectLink.vue");
const SyncBalance = () =>
  import("@/modules/Customer/components/syncBalance.vue");
const Statement = () => import("@/modules/Customer/components/Statement");
const ParentInvoiceCreate = () =>
  import("@/modules/Customer/components/ParentInvoiceCreate.vue");
const Subscription = () => import("@/modules/Subscription/components/List.vue");
const Edit = () => import("@/modules/Customer/components/Edit");
const TuitionBillingPreview = () =>
  import("@/modules/Customer/components/TuitionBillingPreview.vue");
const ReEnrollment = () =>
  import("@/modules/Customer/components/ReEnrollment.vue");

export default {
  name: "customer-profile",
  components: {
    Edit,
    StudentList,
    PaymentCollectLink,
    Subscription,
    SyncBalance,
    Dashboard,
    Statement,
    InvoiceList,
    AttachmentList,
    AdvancePaymentList,
    PaymentList,
    ParentInvoiceCreate,
    TuitionBillingPreview,
    ReEnrollment,
  },
  data() {
    return {
      active: 0,
      adjacentParents: { previous: null, next: null },
      parentIdDialog: false,
      parentIdInput: "",
      parentIdLoading: false,
      createParentInvoiceDialog: false,
      editDialog: false,
      billingPreviewDialog: false,
      reEnrollmentDialog: false,
      id: null,
      loading: true,
      studentFee: { totalCustomerFee: 0, totalCharity: 0, totalZakat: 0, totalPayrollDeduction: 0, totalDiscount: 0 },
      advanceSummary: { received: 0, applied: 0, available: 0 },
      tabs: [
        { label: "Overview", icon: "mdi-view-dashboard-outline" },
        { label: "Students", icon: "mdi-account-school-outline" },
        { label: "Invoices", icon: "mdi-receipt-text-outline" },
        { label: "Payments", icon: "mdi-cash-check" },
        { label: "Advance payments", icon: "mdi-cash-fast" },
        { label: "Subscriptions", icon: "mdi-calendar-sync-outline" },
        { label: "Files", icon: "mdi-paperclip" },
      ],
    };
  },
  computed: {
    ...mapGetters("customer", ["getCustomer"]),
    ...mapGetters("global", ["checkRightStatus"]),
    isOverLimit() {
      return (
        Number(this.getCustomer.balance) > Number(this.getCustomer.balanceLimit)
      );
    },
    paymentMethods() {
      const methods = [];
      const merchantMethods = [];
      if (this.getCustomer.payingCash) methods.push("Cash");
      (this.getCustomer.MerchantCustomers || []).forEach((mapping) => {
        const account = mapping.MerchantAccount ||
          (mapping.DonationCategory && mapping.DonationCategory.MerchantAccount);
        if (!account || !account.provider) return;
        const provider = String(account.provider).toUpperCase();
        merchantMethods.push(provider === "PAYCOMPASS" ? "PayCompass" :
          provider === "STRIPE" ? "Stripe" : String(account.provider));
      });
      methods.push(...merchantMethods);
      if (!merchantMethods.length && this.getCustomer.stripeId) methods.push("Stripe");
      return [...new Set(methods)];
    },
  },
  watch: {
    "$route.params.id"(next, previous) {
      if (next && next !== previous) this.loadData();
    },
  },
  async created() {
    if (this.$route.params.tab !== undefined)
      this.active = Number(this.$route.params.tab);
    await this.loadData();
    this.$events.listen("customerProfileEvent", this.loadData);
  },
  beforeDestroy() {
    this.$events.remove("customerProfileEvent");
  },
  methods: {
    ...mapActions("customer", ["setCustomer"]),
    initials(name) {
      return (name || "P")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase();
    },
    async navigateCustomer(customer) {
      if (!customer) return;
      await this.$router.push({ path: `/parents/${customer.id}/profile` });
    },
    moveBack() {
      this.navigateCustomer(this.adjacentParents.previous);
    },
    moveNext() {
      this.navigateCustomer(this.adjacentParents.next);
    },
    showParentIdDialog() {
      this.parentIdInput = "";
      this.parentIdDialog = true;
      this.$nextTick(() => this.$refs.parentIdInput && this.$refs.parentIdInput.focus());
    },
    async openParentId() {
      const localId = Number(this.parentIdInput);
      if (!Number.isInteger(localId) || localId <= 0) return;
      this.parentIdLoading = true;
      try {
        const response = await customerService.getByLocalId(localId);
        if (!response.data) {
          await this.$swal("Parent not found", `No parent with ID #${localId} was found.`, "warning");
          return;
        }
        this.parentIdDialog = false;
        await this.navigateCustomer(response.data);
      } catch (error) {
        await this.$swal("Could not open parent", "Please check the ID and try again.", "error");
      } finally {
        this.parentIdLoading = false;
      }
    },
    async loadData() {
      this.loading = true;
      this.id = Number(this.$route.params.id);
      this.$store.commit("customer/SETCUSTOMER", null);
      try {
        if (!Number.isInteger(this.id) || this.id <= 0) return;
        await this.setCustomer(this.id);
        if (!this.getCustomer || !this.getCustomer.id) return;
        await Promise.all([this.getFeeAmount(), this.getAdvanceSummary(), this.getAdjacentParents()]);
      } finally {
        this.loading = false;
      }
    },
    getAdjacentParents() {
      const localId = this.getCustomer && this.getCustomer.localId;
      if (!localId) {
        this.adjacentParents = { previous: null, next: null };
        return Promise.resolve();
      }
      return customerService.getAdjacentByLocalId(localId).then((response) => {
        this.adjacentParents = response.data || { previous: null, next: null };
      });
    },
    getFeeAmount() {
      return customerService.getFeeByCustomerId(this.id).then((response) => {
        this.studentFee = response.data;
      });
    },
    getAdvanceSummary() {
      return advancePaymentService.getSummary(this.id).then((response) => {
        this.advanceSummary = response.data || { received: 0, applied: 0, available: 0 };
      });
    },
    invoiceCreated() {
      this.createParentInvoiceDialog = false;
      this.$events.emit("invoiceListEvent");
      this.loadData();
    },
    reEnrollmentCompleted() {
      this.reEnrollmentDialog = false;
      this.$events.emit("studentListEvent");
      this.$events.emit("invoiceListEvent");
      this.loadData();
    },
    viewOpenInvoice() {
      const route = this.$router.resolve({
        path: `/global/invoice/${hashids.encode(
          this.getCustomer.id
        )}/openInvoice`,
      });
      window.open(route.href, "newwindow", "width=850, height=1100");
    },
    viewBalanceLetter() {
      const route = this.$router.resolve({
        path: `/global/invoice/${hashids.encode(
          this.getCustomer.id
        )}/PendingBalanceLetter`,
      });
      window.open(route.href, "newwindow", "width=850, height=1100");
    },
    openGoogleMap() {
      const customer = this.getCustomer;
      window.open(
        `https://www.google.com/maps/place/${customer.address} ${customer.city},${customer.state} ${customer.zip}`,
        "_blank"
      );
    },
  },
};
</script>

<style scoped>
/* Shared profile layout styles come from app.scss. */

.profile-summary-value.overdue {
  color: #2e7d32;
}

.profile-billing-preview {
  width: 100%;
  margin-top: 18px;
  text-transform: none;
  letter-spacing: 0;
}

.profile-record-navigation__id {
  padding: 4px 6px;
  border-radius: 6px;
  color: inherit;
  font: inherit;
}

.profile-record-navigation__id:hover,
.profile-record-navigation__id:focus-visible {
  background: rgba(46, 125, 50, 0.08);
  outline: none;
}

.profile-summary-advance {
  color: #2e7d32;
  border-top: 1px solid rgba(46, 125, 50, 0.16);
  padding-top: 10px;
}

@media (max-width: 700px) {
  .profile-hero__quick-invoice-action {
    flex: 1;
  }

  .profile-hero__edit-action {
    min-width: 42px;
    padding: 0 11px;
  }
}

@media (max-width: 420px) {
  .profile-hero__edit-action .v-icon {
    margin: 0 !important;
  }

  .profile-hero__edit-action {
    font-size: 0;
  }
}
</style>
